import { FC, useState } from 'react'
import { Col } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  EmployeeNameSearchInfo,
  EmployeeWithBuFragment,
  ReviewerOfEmployeeType,
  ReviewerOfEmployeesInput,
  ReviewerAndEmployee,
} from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { PerModal } from '../../../components/custom_components/Modal'
import { hasPermission } from '../../../shared/permissions/permission-helper'
import { CompanySettingsPermissions } from '../permissions'
import { EmployeeSearchSelect } from '../../../components/custom_components/EmployeeSearchSelect'
import { EmployeeReviewerSelect } from '../../../components/custom_components/EmployeeReviewerSelect'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { getPossibleRevieweesByReviewerId } from '../../../graphql/reviewer/useRevieweesByReviewerId'
import { CompanyCountrySelectorValue } from '../types'
import { createReviewerOfEmployeeRelation } from '../../../graphql/reviewerEmployee/hooks/useMutateReviewerOfEmployee'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import { showNotification } from '../../../components/custom_components/Notification/notification'

interface ReviewerAddModalProps {
  open: boolean
  activeEmployees: EmployeeWithBuFragment[]
  activeEmployeesWithoutPartners: EmployeeWithBuFragment[]
  companyName: string
  companyCountry: CompanyCountrySelectorValue
  existingRelations: ReviewerAndEmployee[]
  onCancel: () => void
}

export const ReviewerAddModal: FC<ReviewerAddModalProps> = ({
  open,
  activeEmployeesWithoutPartners,
  activeEmployees,
  companyName,
  onCancel,
  companyCountry,
  existingRelations,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const userRoles = user.roles

  const [reviewer, setReviewer] = useState<EmployeeNameSearchInfo | EmployeeWithBuFragment | undefined>()
  // employee is actually reviewee but it's too similar so it might be confused with reviewer
  const [employee, setEmployee] = useState<EmployeeWithBuFragment | undefined>()
  const [saving, toggleSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [possibleReviewees, setPossibleReviewees] = useState<EmployeeWithBuFragment[]>([])

  const onSelectReviewer = async (selectedEmployee?: EmployeeNameSearchInfo | EmployeeWithBuFragment) => {
    if (!!selectedEmployee) {
      setReviewer(selectedEmployee)
      setEmployee(undefined) // reset employee in case they are already selected
      await getPossibleRevieweesByReviewerId(selectedEmployee.id, companyCountry.companyId, setPossibleReviewees)
    }
  }
  const saveChanges = async () => {
    if (!!reviewer && !!employee) {
      toggleSaving(true)
      setErrorMessage('')

      const reviewType = activeEmployees.find((emp) => emp.id === reviewer.id)
        ? ReviewerOfEmployeeType.Internal
        : ReviewerOfEmployeeType.External
      const revEmpInput: ReviewerOfEmployeesInput = {
        type: reviewType,
        reviewerId: reviewer.id,
        employeeId: employee.id,
        companyId: companyCountry.companyId,
        fromDate: new Date().toDateString(),
        toDate: null,
      }
      const result = await createReviewerOfEmployeeRelation(revEmpInput)
      if (!!result.errors && !!result.errors.length) {
        const errorKey = result.errors?.[0].extensions?.validationErrors?.[0]?.errorKey
        const errorMessage = errorKey?.startsWith('error.code')
          ? result.errors?.[0].extensions?.validationErrors?.[0]?.message ||
            t('companySettingsView.reviewerTab.addModal.notifications.error.description')
          : t('companySettingsView.reviewerTab.addModal.notifications.error.description')

        setErrorMessage(errorMessage)
        toggleSaving(false)
      } else {
        resetModalForm()
        showNotification({
          type: 'success',
          message: t('companySettingsView.reviewerTab.addModal.notifications.success.title'),
          description: t('companySettingsView.reviewerTab.addModal.notifications.success.description'),
        })
      }
    }
  }

  const reviewerRelationAlreadyExists = existingRelations.some(
    (r) => reviewer !== undefined && employee !== undefined && r.reviewer.id === reviewer.id && r.employee.id === employee.id,
  )
  const inverseReviewerRelationAlreadyExists = existingRelations.some(
    (r) => reviewer !== undefined && employee !== undefined && r.reviewer.id === employee.id && r.employee.id === reviewer.id,
  )

  const resetModalForm = () => {
    setReviewer(undefined)
    setEmployee(undefined)
    setPossibleReviewees([])
    setErrorMessage('')
    toggleSaving(false)
    onCancel()
  }

  const hasUserFullSearchPermission = hasPermission(CompanySettingsPermissions.reviewerFullSearch, userRoles ? userRoles : [])

  return (
    <PerModal
      title={{ heading: `${t('companySettingsView.reviewerTab.addModal.heading')} ${companyName}` }}
      size="large"
      bodyStyle={{ overflowY: 'auto' }}
      visible={open}
      onCancel={resetModalForm}
      footer={{
        cancelBtn: {
          onClick: resetModalForm,
        },
        confirmBtn: {
          onClick: saveChanges,
          disabled: !employee || reviewerRelationAlreadyExists || saving,
          isProcessing: { active: saving },
          tooltip:
            (!reviewer && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipReviewerAndEmployeeMissing')) ||
            (!employee && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipEmployeeMissing')) ||
            (reviewerRelationAlreadyExists && t('companySettingsView.reviewerTab.addModal.saveBtn.tooltipAlreadyExistsRelation')),
        },
      }}
    >
      <>
        <PerFlexRow direction="row" mb="s">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('companySettingsView.reviewerTab.addModal.reviewer.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            {hasUserFullSearchPermission ? (
              <EmployeeSearchSelect
                selectedValue={reviewer as EmployeeNameSearchInfo | undefined} // this is okay since the HR it will always receive be EmployeeNameSearchInfo
                onSelectValue={onSelectReviewer}
                notFoundContentText={t('companySettingsView.reviewerTab.addModal.reviewer.notFoundSearchSelectContent')}
                placeholderText={t('companySettingsView.reviewerTab.addModal.reviewer.placeholder')}
              />
            ) : (
              <EmployeeReviewerSelect
                placeholderText={t('companySettingsView.reviewerTab.addModal.reviewer.placeholder')}
                notFoundContentText={t('companySettingsView.reviewerTab.addModal.reviewer.notFoundContent')}
                employeeId={reviewer ? reviewer.id : undefined}
                employeeData={activeEmployeesWithoutPartners}
                onChangeEmployee={(employee) => onSelectReviewer(employee)}
              />
            )}
          </Col>
        </PerFlexRow>

        <PerFlexRow direction="row">
          <Col span={4}>
            <PerFlexRow align="middle" className="fullHeight">
              <PerText color="secondary">{t('companySettingsView.reviewerTab.addModal.employee.label')}</PerText>
            </PerFlexRow>
          </Col>
          <Col span={20}>
            <EmployeeReviewerSelect
              placeholderText={t('companySettingsView.reviewerTab.addModal.employee.placeholder')}
              notFoundContentText={t('companySettingsView.reviewerTab.addModal.employee.notFoundContent')}
              employeeId={employee ? employee.id : undefined}
              employeeData={possibleReviewees}
              onChangeEmployee={(employee?: EmployeeWithBuFragment) => {
                if (!!employee) {
                  setEmployee(employee)
                }
              }}
              disabled={!reviewer}
            />
          </Col>
        </PerFlexRow>

        {inverseReviewerRelationAlreadyExists && (
          <PerFlexRow direction="row" align="middle" mt="m">
            <Col span="1">
              <IconIndicator icon="warning_fill" background="warning" size="l" />
            </Col>
            <Col span="23">
              <PerText color="secondary" fontSize="s">
                {t('companySettingsView.reviewerTab.addModal.inverseRelationshipeExists', {
                  reviewer: employee!.firstName,
                  reviewee: hasUserFullSearchPermission
                    ? (reviewer as EmployeeNameSearchInfo).chosenFirstName || (reviewer as EmployeeNameSearchInfo).legalFirstName
                    : (reviewer as EmployeeWithBuFragment).firstName,
                })}
              </PerText>
            </Col>
          </PerFlexRow>
        )}

        {errorMessage && (
          <PerText color="error" fontSize="m" mt="m">
            {errorMessage}
          </PerText>
        )}
      </>
    </PerModal>
  )
}
